import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  AutoFixHigh as AutoPopulateIcon,
  UploadFile as UploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import indService from '../services/ind.service';

// ---------------------------------------------------------------------------
// Enums and block layout (ind-package-spec 02, section 2)
// ---------------------------------------------------------------------------

export const IND_ENUMS = {
  moleculeType: ['small-molecule', 'recombinant-protein', 'monoclonal-antibody', 'peptide', 'oligonucleotide', 'other'],
  center: ['CDER', 'CBER'],
  indType: ['commercial', 'research'],
  referencedApplicationType: ['IND', 'NDA', 'BLA', 'DMF', 'MAF'],
  loaStatus: ['obtained', 'requested', 'not-needed'],
  studyType: [
    'primary-pharmacodynamics', 'secondary-pharmacodynamics', 'safety-pharmacology', 'pharmacokinetics-adme',
    'single-dose-toxicity', 'repeat-dose-toxicity', 'genotoxicity', 'carcinogenicity',
    'reproductive-developmental-toxicity', 'local-tolerance', 'immunotoxicity', 'phototoxicity',
    'abuse-liability', 'other',
  ],
  reportStatus: ['final', 'draft-unaudited', 'in-progress'],
  fihMethod: ['NOAEL-HED', 'MABEL', 'STD10', 'HNSTD', 'other'],
  priorStatus: ['none', 'investigational-abroad', 'marketed-abroad', 'related-compounds', 'prior-us-ind'],
  claimType: ['categorical-exclusion-25.31(e)', 'environmental-assessment'],
};

const BIOLOGIC_MOLECULE_TYPES = new Set(['recombinant-protein', 'monoclonal-antibody', 'peptide', 'oligonucleotide']);

const EDITABLE_BLOCKS = [
  'product', 'sponsor', 'regulatory', 'clinicalPlan', 'nonclinical', 'fihDose',
  'priorHumanExperience', 'additionalInformation', 'environmental', 'investigatorsBrochure',
];

// Field keys that get a multiline text box (long prose) rather than a single line.
const MULTILINE_KEY_RE = /(Summary|Notes|Findings|Statement|Description|Rationale|Justification|Scheme|Inputs|Approach|Points|Implications|Objectives|Similarities|Reason|Advice)$/i;
const MULTILINE_KEYS = new Set([
  'rationale', 'description', 'other', 'mechanismOfAction', 'excipients', 'activeIngredients',
  'storageConditions', 'toxLotVsClinicalLot', 'targetOrgans', 'reversibility', 'design', 'doses',
  'cnsActivity', 'plannedTiming', 'digitalHealthTechnology', 'companionDiagnostic', 'extraordinaryCircumstances',
]);
const isMultilineKey = (key) => {
  const leaf = String(key || '').split('.').pop();
  return MULTILINE_KEYS.has(leaf) || MULTILINE_KEY_RE.test(leaf);
};

const humanize = (key) =>
  String(key || '')
    .split('.')
    .pop()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

// Field descriptor helpers. `type` defaults to text / multiline based on the key.
const f = (key, label, extra = {}) => ({ key, label: label || humanize(key), ...extra });
const sel = (key, options, label) => f(key, label, { type: 'select', options });
const sw = (key, label) => f(key, label, { type: 'switch' });

const NONCLINICAL_STUDY_COLUMNS = [
  f('studyId', 'Study ID'),
  sel('studyType', IND_ENUMS.studyType, 'Study type'),
  f('title', 'Title'),
  f('species', 'Species'),
  f('strain', 'Strain'),
  f('sexAndNumber', 'Sex and number'),
  f('route', 'Route'),
  f('doses', 'Doses'),
  f('dosingDuration', 'Dosing duration'),
  f('recoveryPeriod', 'Recovery period'),
  sw('glpCompliant', 'GLP compliant'),
  f('glpStatement', 'GLP statement'),
  sel('reportStatus', IND_ENUMS.reportStatus, 'Report status'),
  f('testingFacility', 'Testing facility'),
  f('reportLocation', 'Report location'),
  f('noael', 'NOAEL'),
  f('noaelUnits', 'NOAEL units'),
  f('exposureAtNoael', 'Exposure at NOAEL'),
  f('keyFindings', 'Key findings'),
  f('targetOrgans', 'Target organs'),
  f('reversibility', 'Reversibility'),
  f('testArticleLot', 'Test article lot'),
];

const BLOCKS = [
  {
    key: 'product',
    title: 'Product',
    items: [
      { kind: 'fields', fields: [
        f('code', 'Product code'), f('inn', 'INN'), f('otherNames'), f('activeIngredients'),
        f('pharmacologicClass'), f('mechanismOfAction'),
        sel('moleculeType', IND_ENUMS.moleculeType, 'Molecule type'),
        f('structuralFormulaDescription'), f('molecularFormula'), f('molecularWeight'),
        f('dosageForm'), f('strength'), f('route'), f('formulationDescription'), f('excipients'),
        f('storageConditions'), f('structuralSimilarities'),
      ] },
    ],
  },
  {
    key: 'sponsor',
    title: 'Sponsor',
    items: [
      { kind: 'fields', fields: [
        f('name', 'Sponsor name'), f('address'), f('contactName'), f('contactTitle'),
        f('contactPhone'), f('contactEmail'), f('usAgent', 'US agent'),
      ] },
    ],
  },
  {
    key: 'regulatory',
    title: 'Regulatory',
    items: [
      { kind: 'fields', fields: [
        sel('center', IND_ENUMS.center, 'Center'), f('reviewDivision'), f('pindNumber', 'PIND number'),
        f('preIndMeetingDate', 'Pre-IND meeting date'), f('preIndAdviceSummary', 'Pre-IND advice summary'),
        sel('indType', IND_ENUMS.indType, 'IND type'),
      ] },
      { kind: 'table', path: 'referencedApplications', title: 'Referenced applications', columns: [
        sel('type', IND_ENUMS.referencedApplicationType, 'Type'), f('number'), f('holder'),
        sel('loaStatus', IND_ENUMS.loaStatus, 'LOA status'),
      ] },
    ],
  },
  {
    key: 'clinicalPlan',
    title: 'Clinical plan',
    items: [
      { kind: 'fields', fields: [
        f('indication'), f('phase'), f('therapeuticArea'), f('rationale'), f('developmentApproach'),
        f('keyDecisionPoints'), f('totalEstimatedSubjects'), f('plannedDurationMonths'),
      ] },
      { kind: 'table', path: 'firstYearTrials', title: 'First-year trials', columns: [
        f('title'), f('design'), f('population'), f('estimatedSubjects'), f('durationWeeks', 'Duration (weeks)'), f('objectives'),
      ] },
    ],
  },
  {
    key: 'nonclinical',
    title: 'Nonclinical',
    items: [
      { kind: 'table', path: 'studies', title: 'Nonclinical studies', columns: NONCLINICAL_STUDY_COLUMNS },
      { kind: 'fields', fields: [
        f('pharmacologySummary'), f('safetyPharmacologySummary'), f('pkAdmeSummary', 'PK/ADME summary'),
        f('toxicologySummary'), f('genotoxicitySummary'), f('reproductiveToxicitySummary'), f('otherToxicitySummary'),
        f('toxLotVsClinicalLot', 'Tox lot vs clinical lot'), f('speciesRelevanceJustification'), f('overviewNotes'),
      ] },
    ],
  },
  {
    key: 'fihDose',
    title: 'First-in-human dose',
    items: [
      { kind: 'fields', fields: [
        sel('method', IND_ENUMS.fihMethod, 'Method'), f('mostSensitiveSpecies'), f('noael', 'NOAEL'),
        f('hedConversionFactor', 'HED conversion factor'), f('hed', 'HED'), f('safetyFactor'), f('mrsd', 'MRSD'),
        f('pad', 'PAD'), f('mabelInputs', 'MABEL inputs'), f('proposedStartingDose'), f('maxPlannedDose'),
        f('exposureCapRationale'), f('escalationScheme'), f('stoppingRulesSummary'), f('justificationNotes'),
      ] },
    ],
  },
  {
    key: 'priorHumanExperience',
    title: 'Prior human experience',
    items: [
      { kind: 'fields', fields: [sel('status', IND_ENUMS.priorStatus, 'Status'), f('summary')] },
      { kind: 'table', path: 'studies', title: 'Prior clinical studies', columns: [
        f('identifier'), f('country'), f('design'), f('subjects'), f('doses'), f('exposureDuration'),
        f('pkFindings', 'PK findings'), f('safetyFindings'), f('efficacyFindings'),
      ] },
      { kind: 'table', path: 'marketing', title: 'Marketing history', columns: [
        f('country'), f('status'), f('dateApproved'), sw('withdrawn', 'Withdrawn'), f('withdrawalReason'),
      ] },
      { kind: 'fields', fields: [f('literatureSummary'), f('implications')] },
    ],
  },
  {
    key: 'additionalInformation',
    title: 'Additional information',
    items: [
      { kind: 'fields', fields: [
        sw('abusePotential.applicable', 'Abuse potential: applicable'),
        f('abusePotential.cnsActivity', 'Abuse potential: CNS activity'),
        f('abusePotential.assessmentSummary', 'Abuse potential: assessment summary'),
        f('abusePotential.schedulingStatus', 'Abuse potential: scheduling status'),
        sw('radioactive.applicable', 'Radioactive: applicable'),
        f('radioactive.dosimetrySummary', 'Radioactive: dosimetry summary'),
        f('pediatric.statement', 'Pediatric: statement'),
        f('pediatric.plannedTiming', 'Pediatric: planned timing'),
        sw('combinationProduct.applicable', 'Combination product: applicable'),
        f('combinationProduct.description', 'Combination product: description'),
        f('digitalHealthTechnology'), f('companionDiagnostic'), f('other'),
      ] },
    ],
  },
  {
    key: 'environmental',
    title: 'Environmental',
    items: [
      { kind: 'fields', fields: [
        sel('claimType', IND_ENUMS.claimType, 'Claim type'), f('extraordinaryCircumstances'), f('notes'),
      ] },
    ],
  },
  {
    key: 'investigatorsBrochure',
    title: "Investigator's Brochure metadata",
    items: [
      { kind: 'fields', fields: [
        f('editionNumber'), f('releaseDate'), f('supersededEdition'), f('dataCutoffDate'), f('confidentialityStatement'),
      ] },
    ],
  },
];

// ---------------------------------------------------------------------------
// Small immutable path helpers
// ---------------------------------------------------------------------------

const getAt = (obj, path) =>
  String(path).split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);

function setAt(obj, path, value) {
  const keys = String(path).split('.');
  const root = { ...(obj || {}) };
  let cursor = root;
  keys.forEach((k, i) => {
    if (i === keys.length - 1) {
      cursor[k] = value;
    } else {
      cursor[k] = { ...(cursor[k] || {}) };
      cursor = cursor[k];
    }
  });
  return root;
}

const deriveProductType = (intake) =>
  BIOLOGIC_MOLECULE_TYPES.has(intake?.product?.moleculeType) ? 'biologic' : 'small-molecule';

// ---------------------------------------------------------------------------
// Field renderers
// ---------------------------------------------------------------------------

function FieldInput({ field, value, onChange, size, label }) {
  const effectiveLabel = label === undefined ? field.label : label;
  if (field.type === 'switch') {
    return (
      <FormControlLabel
        control={<Switch size="small" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />}
        label={effectiveLabel || ''}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <FormControl fullWidth size={size}>
        {effectiveLabel ? <InputLabel>{effectiveLabel}</InputLabel> : null}
        <Select
          value={value ?? ''}
          label={effectiveLabel || undefined}
          displayEmpty={!effectiveLabel}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value=""><em>Not set</em></MenuItem>
          {(field.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  const multiline = field.type === 'multiline' || isMultilineKey(field.key);
  return (
    <TextField
      fullWidth
      size={size}
      label={effectiveLabel || undefined}
      value={value ?? ''}
      multiline={multiline}
      minRows={multiline ? (size === 'small' ? 1 : 2) : undefined}
      maxRows={multiline ? 8 : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

FieldInput.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string,
  label: PropTypes.string,
};

function EditableTable({ title, columns, rows, onChange }) {
  const list = Array.isArray(rows) ? rows : [];
  const updateRow = (index, key, value) =>
    onChange(list.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  const addRow = () => onChange([...list, {}]);
  const removeRow = (index) => onChange(list.filter((_, i) => i !== index));

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">{title} ({list.length})</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add row</Button>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
        <Table size="small" stickyHeader sx={{ minWidth: columns.length * 170 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>#</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ minWidth: isMultilineKey(col.key) ? 240 : 160, whiteSpace: 'nowrap' }}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ color: 'text.secondary', py: 2 }}>
                  No rows yet.
                </TableCell>
              </TableRow>
            ) : (
              list.map((row, index) => (
                <TableRow key={row._id || index}>
                  <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ verticalAlign: 'top' }}>
                      <FieldInput
                        field={col}
                        label=""
                        size="small"
                        value={row[col.key]}
                        onChange={(v) => updateRow(index, col.key, v)}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Tooltip title="Remove row">
                      <IconButton size="small" onClick={() => removeRow(index)} aria-label="remove row">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

EditableTable.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

const INDIntakeForm = ({ projectId }) => {
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // 'auto' | 'extract' | null
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set([BLOCKS[0].key]));
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await indService.getIntake(projectId);
      setIntake(data?.intake || {});
      setDirty(false);
    } catch (err) {
      console.error('INDIntakeForm: failed to load intake', err);
      setError(err.message || 'Failed to load IND intake');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const buildPayload = (source) => {
    const payload = {};
    EDITABLE_BLOCKS.forEach((block) => {
      if (source && source[block] !== undefined) payload[block] = source[block];
    });
    return payload;
  };

  const save = async (source = intake) => {
    setSaving(true);
    setError(null);
    try {
      const data = await indService.updateIntake(projectId, buildPayload(source));
      if (data?.intake) setIntake(data.intake);
      setDirty(false);
      return true;
    } catch (err) {
      console.error('INDIntakeForm: save failed', err);
      setError(err.message || 'Failed to save IND intake');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (await save()) setNotice({ severity: 'success', message: 'IND intake saved.' });
  };

  const handleAutoPopulate = async () => {
    if (dirty && !(await save())) return;
    setBusy('auto');
    setError(null);
    try {
      const data = await indService.autoPopulateIntake(projectId);
      if (data?.intake) setIntake(data.intake);
      setDirty(false);
      const count = Array.isArray(data?.populatedFields) ? data.populatedFields.length : 0;
      setNotice({
        severity: 'success',
        message: count > 0 ? `Auto-populated ${count} field${count === 1 ? '' : 's'} from the study.` : 'No empty fields could be populated from the study.',
      });
    } catch (err) {
      console.error('INDIntakeForm: auto-populate failed', err);
      setError(err.message || 'Auto-populate failed');
    } finally {
      setBusy(null);
    }
  };

  const handleFilesChosen = async (event) => {
    const files = Array.from(event.target.files || []);
    if (event.target) event.target.value = '';
    if (files.length === 0) return;
    if (dirty && !(await save())) return;
    setBusy('extract');
    setError(null);
    try {
      const data = await indService.extractNonclinical(projectId, files);
      if (data?.intake) setIntake(data.intake);
      setDirty(false);
      setExpanded((prev) => new Set(prev).add('nonclinical'));
      const added = Number(data?.studiesAdded || 0);
      setNotice({
        severity: 'success',
        message: `Extracted ${added} nonclinical stud${added === 1 ? 'y' : 'ies'} from ${files.length} file${files.length === 1 ? '' : 's'}.`,
      });
    } catch (err) {
      console.error('INDIntakeForm: nonclinical extraction failed', err);
      setError(err.message || 'Nonclinical extraction failed');
    } finally {
      setBusy(null);
    }
  };

  const setValue = (path, value) => {
    setIntake((prev) => setAt(prev || {}, path, value));
    setDirty(true);
  };

  const toggleExpanded = (key) => (_, isOpen) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(key); else next.delete(key);
      return next;
    });
  };

  if (!projectId) {
    return <Alert severity="info">Select a study to edit its IND intake.</Alert>;
  }
  if (loading && !intake) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const productType = intake?.productType || deriveProductType(intake);
  const sourceFiles = Array.isArray(intake?.sourceFiles) ? intake.sourceFiles : [];
  const anyBusy = saving || Boolean(busy);

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        multiple
        hidden
        onChange={handleFilesChosen}
      />

      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6">IND Intake</Typography>
          <Tooltip title="Derived from Product > Molecule type; selects prompt variants during generation">
            <Chip
              size="small"
              color={productType === 'biologic' ? 'secondary' : 'primary'}
              variant="outlined"
              label={`Product type: ${productType}`}
            />
          </Tooltip>
          {dirty && <Chip size="small" color="warning" label="Unsaved changes" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={busy === 'auto' ? <CircularProgress size={16} /> : <AutoPopulateIcon />}
            disabled={anyBusy}
            onClick={handleAutoPopulate}
          >
            Auto-populate from study
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={busy === 'extract' ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={anyBusy}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            Upload nonclinical reports and extract
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
            disabled={anyBusy}
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {BLOCKS.map((block) => (
        <Accordion
          key={block.key}
          expanded={expanded.has(block.key)}
          onChange={toggleExpanded(block.key)}
          TransitionProps={{ unmountOnExit: true }}
          disableGutters
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{block.title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {block.items.map((item, itemIdx) =>
              item.kind === 'table' ? (
                <EditableTable
                  key={`${block.key}.${item.path}`}
                  title={item.title}
                  columns={item.columns}
                  rows={getAt(intake, `${block.key}.${item.path}`)}
                  onChange={(rows) => setValue(`${block.key}.${item.path}`, rows)}
                />
              ) : (
                <Grid container spacing={2} key={`${block.key}.fields.${itemIdx}`} sx={{ mb: 2 }}>
                  {item.fields.map((field) => {
                    const multiline = field.type !== 'select' && field.type !== 'switch' && isMultilineKey(field.key);
                    return (
                      <Grid item xs={12} md={multiline ? 12 : 6} lg={multiline ? 12 : 4} key={field.key}>
                        <FieldInput
                          field={field}
                          size="small"
                          value={getAt(intake, `${block.key}.${field.key}`)}
                          onChange={(v) => setValue(`${block.key}.${field.key}`, v)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )
            )}
            {block.key === 'nonclinical' && sourceFiles.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Uploaded source files:</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {sourceFiles.map((file, i) => (
                    <Chip key={file._id || `${file.originalName}-${i}`} size="small" variant="outlined" label={`${file.originalName || 'file'}${file.category ? ` (${file.category})` : ''}`} />
                  ))}
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.severity || 'info'} onClose={() => setNotice(null)} variant="filled">
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

INDIntakeForm.propTypes = {
  projectId: PropTypes.string,
};

export default INDIntakeForm;
